import { inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { Amplify } from 'aws-amplify'
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  rememberDevice,
  resetPassword,
  signIn,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth'
import { environment } from '../../../environments/environment'
import { CompanyPolicies, OfferPolicies, Role } from '@onedv-portal/shared'

export interface AuthUser {
  username: string
  email?: string
  sub: string
  role: Role | undefined
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router)
  private readonly currentUserSignal = signal<AuthUser | null>(null)
  private offerPoliciesService: OfferPolicies | null = null
  private companyPoliciesService: CompanyPolicies | null = null

  constructor() {
    this.initializeAmplify()
  }

  canCreateOffer(): boolean {
    return this.offerPoliciesService?.canCreate() ?? false
  }

  canDeleteOffer(): boolean {
    return this.offerPoliciesService?.canDelete() ?? false
  }

  canReadOffer(): boolean {
    return this.offerPoliciesService?.canRead() ?? false
  }

  canManageCompanies(): boolean {
    return this.companyPoliciesService?.canCreate() ?? false
  }

  canReadCompany(): boolean {
    return this.companyPoliciesService?.canRead() ?? false
  }

  canListCompanies(): boolean {
    return this.companyPoliciesService?.canList() ?? false
  }

  get roleLabel(): string {
    switch (this.offerPoliciesService?.getRole()) {
      case Role.Admin:
        return 'Admin'
      case Role.User:
        return 'Nutzer'
      default:
        return ''
    }
  }

  private initializeAmplify(): void {
    const config = {
      Auth: {
        Cognito: {
          userPoolId: environment.auth.userPoolId,
          userPoolClientId: environment.auth.userPoolClientId,
        },
      },
    }

    Amplify.configure(config)
  }

  async initializeAuth(): Promise<void> {
    await this.checkAuthState()
  }

  private async checkAuthState(): Promise<void> {
    try {
      const session = await fetchAuthSession()
      const user = await getCurrentUser()
      const idToken = session.tokens?.idToken?.payload
      const groups = idToken?.['cognito:groups'] as string[] | string
      this.offerPoliciesService = new OfferPolicies(groups)
      this.companyPoliciesService = new CompanyPolicies(groups)

      this.currentUserSignal.set({
        username: user.username,
        email: idToken?.email as string | undefined,
        sub: user.userId,
        role: this.offerPoliciesService.getRole(),
      })
    } catch {
      this.currentUserSignal.set(null)
      this.offerPoliciesService = null
      this.companyPoliciesService = null
    }
  }

  async login(email: string, password: string): Promise<SignInOutput> {
    const result = await signIn({
      username: email,
      password,
    })

    if (result.isSignedIn) {
      await this.checkAuthState()
    }

    return result
  }

  async confirmNewPassword(newPassword: string): Promise<SignInOutput> {
    const result = await confirmSignIn({
      challengeResponse: newPassword,
    })

    if (result.isSignedIn) {
      await this.checkAuthState()
    }

    return result
  }

  async logout(): Promise<void> {
    await signOut()
    this.currentUserSignal.set(null)
    this.offerPoliciesService = null
    this.companyPoliciesService = null
    await this.router.navigate(['/login'])
  }

  async getIdToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString()
    } catch {
      return undefined
    }
  }

  async isAuthenticated(): Promise<boolean> {
    await this.checkAuthState()
    return !!this.currentUserSignal()
  }

  async resetPassword(email: string): Promise<void> {
    await resetPassword({ username: email })
  }

  async confirmResetPassword(email: string, confirmationCode: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username: email, confirmationCode, newPassword })
  }

  readonly currentUser = this.currentUserSignal.asReadonly()

  async confirmSignInWithTOTP(code: string): Promise<SignInOutput> {
    return await confirmSignIn({
      challengeResponse: code.trim(),
    })
  }

  async rememberDevice() {
    await rememberDevice()
  }
}
